import type { LucideIcon } from "lucide-react";
import { Handshake, Inbox, ListTodo, Settings, Users } from "lucide-react";
import { matchPath } from "react-router";

/**
 * The main menu, shared by the desktop header and the mobile bottom bar.
 *
 * Owner's rule (04.11): at most five items — Лиды, Сделки, Клиенты, Задачи and
 * Настройки (owner only). Лиды come first (Task 6). «Сегодня» is
 * the home page: the logo leads there on desktop, the first button on mobile.
 * On a phone the last button is the personal page (profile, theme, log out).
 */
export type NavItem = {
  key: string;
  to: string;
  /** translation key, with smart_count 2 for resource names */
  label: string;
  icon: LucideIcon;
  /** route patterns that highlight the item */
  match: string[];
  /** shown only when the authProvider allows it */
  access?: { resource: string; action: string };
  /** the company settings have no phone version */
  desktopOnly?: boolean;
  /** part of the CRM module: hidden with configuration.config.modules.crm = false */
  crm?: boolean;
};

export const navItems: NavItem[] = [
  {
    key: "leads",
    to: "/leads",
    // not the plural of resources.leads.name: with smart_count 2 Russian
    // gives «Лида» (два лида), the menu needs «Лиды»
    label: "crm.navigation.leads",
    icon: Inbox,
    match: ["/leads/*"],
    crm: true,
  },
  {
    key: "deals",
    to: "/deals",
    label: "resources.deals.name",
    icon: Handshake,
    match: ["/deals/*"],
    crm: true,
  },
  {
    key: "clients",
    to: "/contacts",
    label: "crm.navigation.clients",
    icon: Users,
    match: ["/contacts/*", "/companies/*"],
    crm: true,
  },
  {
    key: "tasks",
    to: "/tasks",
    label: "resources.tasks.name",
    icon: ListTodo,
    match: ["/tasks/*"],
    crm: true,
  },
  {
    key: "settings",
    to: "/settings",
    label: "crm.settings.title",
    icon: Settings,
    match: ["/settings/*", "/sales/*"],
    access: { resource: "configuration", action: "edit" },
    desktopOnly: true,
  },
];

/** the menu for this company: without the CRM items when the CRM module is off
 * (a client who keeps their own CRM gets the dashboard and Settings) */
export const visibleNavItems = (crmModule: boolean) =>
  crmModule ? navItems : navItems.filter((item) => !item.crm);

export const findActiveNavItem = (pathname: string) =>
  navItems.find((item) =>
    item.match.some((pattern) => matchPath(pattern, pathname)),
  )?.key;
